import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock, Unlock, Trash2, MessageCircle, CheckCircle2, Link as LinkIcon, ExternalLink, UserPlus, X, Search, ClipboardList } from 'lucide-react';
import API_URL from '../config';
import LoadingScreen from '../components/LoadingScreen';
import ReviewersModal from '../components/ReviewersModal';
import './Profile.css';

export default function AdminTeams() {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const [task, setTask] = useState(null);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showReviewersModal, setShowReviewersModal] = useState(false);

    const [showGenerateModal, setShowGenerateModal] = useState(false);

    // Member management state
    const [selectedMember, setSelectedMember] = useState(null); // { email, name, fromTeamId, fromTeamName }
    const [addMemberTeamId, setAddMemberTeamId] = useState(null); // which team's + is open
    const [addSearch, setAddSearch] = useState('');
    const [addSearchResult, setAddSearchResult] = useState(null); // { found: bool, user: obj }
    const [addSearchLoading, setAddSearchLoading] = useState(false);
    const [addSearchError, setAddSearchError] = useState('');
    const addSearchRef = useRef(null);

    // Generation Filter State
    const [deptOptions, setDeptOptions] = useState([]); // Populated from DB
    const [yearOptions, setYearOptions] = useState(["1", "2", "3", "4"]);
    const [secOptions, setSecOptions] = useState(["A", "B", "C", "D"]);

    const [selectedDepts, setSelectedDepts] = useState([]);
    const [selectedYears, setSelectedYears] = useState([]);
    const [selectedSecs, setSelectedSecs] = useState([]);

    const [isGenerating, setIsGenerating] = useState(false);
    const [justGenerated, setJustGenerated] = useState(false); // New state for Save/Revert flow
    const [zipProgress, setZipProgress] = useState(null);
    const zipLogsEndRef = useRef(null);

    useEffect(() => {
        fetchData();
        fetchDepartments();
    }, [taskId]);

    const fetchDepartments = async () => {
        try {
            const res = await fetch(`${API_URL}/utils/departments`);
            const data = await res.json();
            if (data.departments && Array.isArray(data.departments)) {
                setDeptOptions(data.departments);
            }
        } catch (err) {
            console.error("Failed to fetch departments", err);
            setDeptOptions(["CSE", "IT", "ECE", "EEE", "Mech", "Civil", "EIE", "AIML", "IoT", "Data Science"]);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const user = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');

            // 1. Try active/locked tasks first
            const taskRes = await fetch(`${API_URL}/formation/tasks?email=${user.email || ''}`);
            const taskData = await taskRes.json();
            let currentTask = (taskData.tasks || []).find(t => String(t.id) === taskId);

            // 2. If not found in active, check closed tasks (admin viewing past formations)
            if (!currentTask) {
                const closedRes = await fetch(`${API_URL}/formation/tasks/closed?email=${user.email || ''}`);
                if (closedRes.ok) {
                    const closedData = await closedRes.json();
                    currentTask = (closedData.tasks || []).find(t => String(t.id) === taskId);
                }
            }

            setTask(currentTask || null);

            // Fetch teams for this task regardless of status
            const teamsRes = await fetch(`${API_URL}/formation/tasks/${taskId}/teams`);
            const teamsData = await teamsRes.json();
            setTeams(teamsData.teams || []);
        } catch (err) {
            console.error("Failed to fetch data", err);
        } finally {
            setLoading(false);
        }
    };

    const handleRoomLockToggle = async () => {
        if (!task) return;
        const newStatus = task.status === 'locked' ? 'unlock' : 'lock';

        if (!window.confirm(`Are you sure you want to ${newStatus.toUpperCase()} this rooms team formation?`)) return;

        try {
            const res = await fetch(`${API_URL}/formation/tasks/${taskId}/${newStatus}`, { method: 'POST' });
            if (res.ok) {
                alert(`Room ${newStatus}ed successfully!`);
                fetchData();
            } else {
                alert("Action failed");
            }
        } catch (err) {
            console.error("Lock toggle error", err);
        }
    };

    const handleTeamLockToggle = async (teamId) => {
        try {
            const user = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
            const res = await fetch(`${API_URL}/formation/teams/${teamId}/toggle-lock`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: user.email })
            });
            if (res.ok) fetchData();
            else {
                const err = await res.json();
                alert(err.error || "Failed to toggle lock");
            }
        } catch (err) {
            console.error("Team lock error", err);
        }
    };

    const toggleSelection = (item, list, setList) => {
        if (list.includes(item)) {
            setList(list.filter(i => i !== item));
        } else {
            setList([...list, item]);
        }
    };

    const handleRevert = async () => {
        if (!window.confirm("Are you sure? This will DELETE all auto-generated teams and UNLOCK the room.")) return;

        try {
            const res = await fetch(`${API_URL}/formation/tasks/${taskId}/revert-auto`, { method: 'POST' });
            if (res.ok) {
                alert("Changes reverted!");
                setJustGenerated(false);
                fetchData();
            } else {
                const data = await res.json();
                alert(data.error || "Revert failed");
            }
        } catch (err) {
            console.error(err);
            alert("Revert failed");
        }
    };

    const handleSave = () => {
        setJustGenerated(false);
        alert("Changes saved! Room is now strictly locked.");
    };

    const handleGenerateTeams = async () => {
        if (!window.confirm("This will lock the room and generate teams from eligible students. Continue?")) return;

        setIsGenerating(true);
        // Simulate animation delay for UX
        await new Promise(r => setTimeout(r, 2000));

        try {
            const res = await fetch(`${API_URL}/formation/tasks/${taskId}/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    departments: selectedDepts,
                    sections: selectedSecs,
                    years: selectedYears
                })
            });

            const data = await res.json();
            if (res.ok) {
                // Success: Update lists and show Save/Revert bar
                setShowGenerateModal(false);
                fetchData();
                setJustGenerated(true); // Show the UI
                setIsGenerating(false); // Stop loading
            } else {
                alert(data.error || "Generation failed");
                setIsGenerating(false);
            }
        } catch (err) {
            console.error("Generation error", err);
            setIsGenerating(false);
        }
    };

    const handleDeleteTeam = async (teamId, teamName) => {
        if (!window.confirm(`Are you sure you want to delete team "${teamName}"? This will remove all members.`)) {
            return;
        }

        try {
            const user = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
            const res = await fetch(`${API_URL}/formation/teams/${teamId}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_email: user.email })
            });

            if (res.ok) {
                alert("Team deleted successfully!");
                fetchData(); // Refresh the list
            } else {
                const err = await res.json();
                alert(err.error || "Failed to delete team");
            }
        } catch (err) {
            console.error("Delete error", err);
            alert("Failed to delete team");
        }
    };

    const handleChangeLead = async (teamId, newLeadEmail, teamName) => {
        if (!window.confirm(`Are you sure you want to make ${newLeadEmail} the lead of "${teamName}"?`)) {
            return;
        }

        try {
            const user = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
            const res = await fetch(`${API_URL}/formation/teams/change-lead`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    team_id: teamId,
                    new_lead_email: newLeadEmail,
                    requester_email: user.email
                })
            });

            if (res.ok) {
                alert("Lead changed successfully!");
                fetchData(); // Refresh the list
            } else {
                const err = await res.json();
                alert(err.error || "Failed to change lead");
            }
        } catch (err) {
            console.error("Change lead error", err);
            alert("Failed to change lead");
        }
    };

    // ── Member management handlers ────────────────────────────────────────
    const handleSelectMember = (member, team) => {
        if (selectedMember && selectedMember.email === member.email && selectedMember.fromTeamId === team.id) {
            setSelectedMember(null); // Deselect if clicking same member
        } else {
            setSelectedMember({ email: member.email, name: member.name, fromTeamId: team.id, fromTeamName: team.team_name });
            setAddMemberTeamId(null); // Close any open + panel
        }
    };

    const handleMoveMember = async (toTeamId) => {
        if (!selectedMember) return;
        const user = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
        try {
            const res = await fetch(`${API_URL}/formation/teams/move-member`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_email: selectedMember.email,
                    from_team_id: selectedMember.fromTeamId,
                    to_team_id: toTeamId,
                    requester_email: user.email
                })
            });
            const data = await res.json();
            if (res.ok) {
                setSelectedMember(null);
                fetchData();
            } else {
                alert(data.error || 'Failed to move member');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to move member');
        }
    };

    const handleAdminAddMember = async (teamId) => {
        if (!addSearch.trim()) return;
        const user = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
        setAddSearchLoading(true);
        setAddSearchError('');
        try {
            const res = await fetch(`${API_URL}/formation/teams/${teamId}/admin-add-member`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ search_value: addSearch.trim(), requester_email: user.email })
            });
            const data = await res.json();
            if (res.ok) {
                setAddMemberTeamId(null);
                setAddSearch('');
                setAddSearchError('');
                fetchData();
            } else {
                setAddSearchError(data.error || 'Not found');
            }
        } catch (err) {
            setAddSearchError('Network error');
        } finally {
            setAddSearchLoading(false);
        }
    };
    // ─────────────────────────────────────────────────────────────────────

    const handleExport = () => {
        window.open(`${API_URL}/formation/tasks/${taskId}/export`, '_blank');
    };

    const handleZipExport = async () => {
        setZipProgress({ taskId, logs: ['⏳ Initiating background export job...'] });
        try {
            const response = await fetch(`${API_URL}/formation/tasks/${taskId}/export-zip/start`, { method: 'POST' });
            if (!response.ok) throw new Error('Failed to start export');
            
            const { jobId } = await response.json();
            const evtSource = new EventSource(`${API_URL}/formation/tasks/export-zip/progress/${jobId}`);
            
            evtSource.addEventListener('log', (e) => {
                const msg = JSON.parse(e.data);
                setZipProgress(prev => prev ? { ...prev, logs: [...prev.logs, msg] } : null);
                setTimeout(() => zipLogsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
            });

            evtSource.addEventListener('DONE', (e) => {
                evtSource.close();
                setZipProgress(prev => prev ? { ...prev, logs: [...prev.logs, '🎉 ZIP is ready! Triggering download...'] } : null);
                
                const a = document.createElement('a');
                a.href = `${API_URL}/formation/tasks/export-zip/download/${jobId}`;
                a.download = `${task.name || 'Teams'}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();

                setTimeout(() => setZipProgress(null), 3000); // auto-close after 3s
            });

            evtSource.addEventListener('ERROR', (e) => {
                evtSource.close();
                const err = JSON.parse(e.data);
                setZipProgress(prev => prev ? { ...prev, logs: [...prev.logs, `❌ Error: ${err.error}`] } : null);
            });

        } catch (e) {
            setZipProgress(prev => prev ? { ...prev, logs: [...prev.logs, `❌ Failed: ${e.message}`] } : null);
        }
    };

    if (loading) return <LoadingScreen message="Admin Panel" />;
    if (!task) return <div style={{ padding: 24 }}>Task not found</div>;

    const ADMIN_EMAILS = ["24071a6691@vnrvjiet.in", "24071a6679@vnrvjiet.in"];
    const userData = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
    const currentUserEmail = (userData.email || '').toLowerCase().trim();
    const isMainAdmin = ADMIN_EMAILS.includes(currentUserEmail);

    // Check if authorized (Admin or Creator/Collaborator)
    const isAuthorized = isMainAdmin || (task && (
        String(task.creator_email).toLowerCase().trim() === currentUserEmail ||
        (task.collaborators && task.collaborators.some(c => String(c.email).toLowerCase().trim() === currentUserEmail))
    ));
    const isClosed = task.status === 'closed';

    const canViewReviewSheet = isAuthorized || (task && task.is_reviewer) || (userData.role === 'faculty') || (userData.role === 'admin');
    const canManageReviewers = isAuthorized;


    return (
        <>
        <div className="home-page-container">
            <div className="home-content-wrapper" style={{ maxWidth: '95%' }}>
                <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button onClick={() => navigate(userData.role === 'faculty' ? '/faculty/admin' : '/admin')} className="btn-profile btn-profile-secondary">
                        ← Back to {userData.role === 'faculty' ? 'Faculty Admin' : 'Admin'}
                    </button>
                    {!isClosed && (
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            {isAuthorized && (
                                <button
                                    onClick={handleRoomLockToggle}
                                    className={`btn-profile ${task.status === 'locked' ? 'btn-profile-primary' : 'btn-profile-secondary'}`}
                                    style={{ border: task.status === 'locked' ? 'none' : '1px solid #ddd' }}
                                >
                                    {task.status === 'locked' ? <><Lock size={16} /> Unlock Room</> : <><Unlock size={16} /> Lock Room</>}
                                </button>
                            )}

                            {(isAuthorized || canViewReviewSheet) && (
                                <button onClick={handleExport} className="btn-profile btn-profile-secondary" style={{ background: '#107c41', color: 'white', border: 'none' }}>
                                    Export Excel
                                </button>
                            )}

                            {isAuthorized && (
                                <button 
                                    onClick={handleZipExport} 
                                    disabled={!!zipProgress}
                                    className="btn-profile btn-profile-secondary" 
                                    style={{ background: '#10B981', color: 'white', border: 'none', opacity: zipProgress ? 0.7 : 1 }}
                                >
                                    {zipProgress ? '⏳ Exporting...' : '📦 Export ZIP'}
                                </button>
                            )}

                            {canViewReviewSheet && (
                                <button
                                    onClick={() => navigate(`/formation/${taskId}/review`)}
                                    className="btn-profile btn-profile-secondary"
                                    style={{ background: 'linear-gradient(135deg,#f59e0b,#ef4444)', color: 'white', border: 'none', display: 'flex', alignItems: 'center', gap: 6 }}
                                >
                                    <ClipboardList size={16} /> Review Marks Sheet
                                </button>
                            )}

                            {canManageReviewers && (
                                <button
                                    onClick={() => setShowReviewersModal(true)}
                                    className="btn-profile btn-profile-secondary"
                                    style={{ border: '1px solid rgba(245,158,11,0.4)', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 6 }}
                                >
                                    <ClipboardList size={16} /> Manage Reviewers
                                </button>
                            )}

                            {isAuthorized && (
                                <button
                                    onClick={() => setShowGenerateModal(true)}
                                    className="btn-profile btn-profile-primary"
                                    style={{ background: 'var(--accent-color)' }}
                                >
                                    Auto-Generate Teams
                                </button>
                            )}
                        </div>
                    )}
                    {isClosed && (
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={handleExport} className="btn-profile btn-profile-secondary" style={{ background: '#107c41', color: 'white', border: 'none' }}>
                                📥 Export Excel
                            </button>
                            <button 
                                onClick={handleZipExport} 
                                disabled={!!zipProgress}
                                className="btn-profile btn-profile-secondary" 
                                style={{ background: '#10B981', color: 'white', border: 'none', opacity: zipProgress ? 0.7 : 1 }}
                            >
                                {zipProgress ? '⏳ Exporting...' : '📦 Export ZIP'}
                            </button>
                        </div>
                    )}
                </div>

                <div className="glass-card" style={{ marginBottom: 24, padding: '1.5rem', borderRadius: '15px', border: `1px solid ${isClosed ? 'rgba(156,163,175,0.3)' : task.status === 'locked' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)'}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                        <h1 className="brand-title" style={{ margin: 0, fontSize: '2rem' }}>Manage Teams — {task.name}</h1>
                        {isClosed && (
                            <span style={{ background: '#374151', color: '#9CA3AF', padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 'bold' }}>🗂 Closed · Read-only</span>
                        )}
                    </div>
                    <p style={{ opacity: 0.7, marginBottom: 0 }}>
                        Max Members: <strong>{task.max_members}</strong> | Total Teams: <strong>{teams.length}</strong> | Status: <strong style={{ color: isClosed ? '#9CA3AF' : task.status === 'locked' ? '#ef4444' : '#22c55e' }}>{task.status.toUpperCase()}</strong>
                    </p>
                </div>

                {/* ── Selection Mode Banner ─────────────────────────────── */}
                {selectedMember && (
                    <div style={{
                        position: 'sticky', top: 12, zIndex: 100, marginBottom: 16,
                        background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                        borderRadius: 14, padding: '12px 20px',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        boxShadow: '0 4px 24px rgba(99,102,241,0.5)',
                        color: '#fff'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{
                                width: 36, height: 36, borderRadius: '50%',
                                background: 'rgba(255,255,255,0.2)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 'bold', fontSize: '1.1rem'
                            }}>
                                {selectedMember.name?.charAt(0) || '?'}
                            </div>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>
                                    Moving: <strong>{selectedMember.name}</strong>
                                </div>
                                <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>
                                    From <em>{selectedMember.fromTeamName}</em> — Click <strong>"Move Here"</strong> on another team
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => setSelectedMember(null)}
                            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: '6px 12px', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
                        >
                            <X size={14} /> Cancel
                        </button>
                    </div>
                )}

                {teams.length === 0 ? (
                    <div style={{ background: '#f9f9f9', padding: 32, borderRadius: 8, textAlign: 'center', color: '#666' }}>
                        No teams created yet
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 16 }}>
                        {teams.map(team => {
                            // Calculate dynamic glow class based on combinations
                            const wa = !!team.whatsapp_link;
                            const ps = !!team.problem_statement;
                            const ppt = !!(team.ppt_links && team.ppt_links !== '[]');

                            let glowClass = "";
                            if (wa && ps && ppt) glowClass = "team-card-glow glow-all";
                            else if (wa && ps) glowClass = "team-card-glow glow-wa-ps";
                            else if (wa && ppt) glowClass = "team-card-glow glow-wa-ppt";
                            else if (ps && ppt) glowClass = "team-card-glow glow-ps-ppt";
                            else if (wa) glowClass = "team-card-glow glow-wa";
                            else if (ps) glowClass = "team-card-glow glow-ps";
                            else if (ppt) glowClass = "team-card-glow glow-ppt";

                            return (
                                <div
                                    key={team.id}
                                    className={`glass-card ${glowClass}`}
                                    style={{
                                        padding: 20,
                                        position: 'relative'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <h3 style={{ margin: 0 }}>
                                                    {team.team_name}
                                                </h3>
                                                {team.is_auto_generated && <span style={{ fontSize: 10, background: '#f97316', color: 'white', padding: '2px 6px', borderRadius: 4 }}>AUTO</span>}
                                            </div>
                                            <p style={{ margin: '4px 0', fontSize: 13, color: '#666', fontWeight: 500 }}>
                                                Team #{team.team_number || team.id}
                                            </p>
                                            {/* Status Icons Row */}
                                            <div style={{ display: 'flex', gap: '8px', margin: '10px 0' }}>
                                                <div className={`status-icon-badge whatsapp ${team.whatsapp_link ? 'active' : ''}`} title={team.whatsapp_link ? "WhatsApp Group Linked" : "No WhatsApp Link"}>
                                                    <MessageCircle size={14} />
                                                </div>
                                                <div className={`status-icon-badge ps ${team.problem_statement ? 'active' : ''}`} title={team.problem_statement ? "Problem Statement Added" : "No Problem Statement"}>
                                                    <CheckCircle2 size={14} />
                                                </div>
                                                <div className={`status-icon-badge ppt ${(team.ppt_links && team.ppt_links !== '[]') ? 'active' : ''}`} title={(team.ppt_links && team.ppt_links !== '[]') ? "PPT Links Shared" : "No PPT Links"}>
                                                    <LinkIcon size={14} />
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <button
                                                onClick={() => handleTeamLockToggle(team.id)}
                                                className="btn-icon"
                                                title={team.status === 'locked' ? "Unlock Team" : "Lock Team"}
                                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: team.status === 'locked' ? '#ef4444' : '#ccc' }}
                                            >
                                                {team.status === 'locked' ? <Lock size={20} /> : <Unlock size={20} />}
                                            </button>
                                            {isAuthorized && (
                                                <button
                                                    className="btn-icon"
                                                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#dc3545' }}
                                                    onClick={() => handleDeleteTeam(team.id, team.team_name)}
                                                    title="Delete Team"
                                                >
                                                    <Trash2 size={20} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: 10 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#666', marginBottom: 6 }}>
                                            <span>Members ({team.members?.length || 0}/{task.max_members})</span>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span>{team.status === 'locked' ? 'Locked' : 'Open'}</span>
                                                {/* "Move Here" button shown when a member is selected from another team */}
                                                {selectedMember && selectedMember.fromTeamId !== team.id && (
                                                    <button
                                                        onClick={() => handleMoveMember(team.id)}
                                                        style={{
                                                            background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                                                            color: '#fff', border: 'none', borderRadius: 20,
                                                            padding: '3px 12px', fontSize: 11, fontWeight: 700,
                                                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                                                            boxShadow: '0 2px 8px rgba(99,102,241,0.4)',
                                                            animation: 'pulse 1.5s infinite'
                                                        }}
                                                    >
                                                        ↓ Move Here
                                                    </button>
                                                )}
                                                {/* + Add member button */}
                                                {isAuthorized && !selectedMember && (
                                                    <button
                                                        onClick={() => {
                                                            setAddMemberTeamId(addMemberTeamId === team.id ? null : team.id);
                                                            setAddSearch('');
                                                            setAddSearchError('');
                                                        }}
                                                        title="Add member to this team"
                                                        style={{
                                                            background: addMemberTeamId === team.id ? '#ef4444' : '#22c55e',
                                                            color: '#fff', border: 'none', borderRadius: '50%',
                                                            width: 22, height: 22, display: 'flex', alignItems: 'center',
                                                            justifyContent: 'center', cursor: 'pointer', fontSize: 14, fontWeight: 700
                                                        }}
                                                    >
                                                        {addMemberTeamId === team.id ? <X size={12} /> : '+'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div style={{ background: '#e5e7eb', height: 4, borderRadius: 2, overflow: 'hidden' }}>
                                            <div style={{ width: `${Math.min(100, ((team.members?.length || 0) / task.max_members) * 100)}%`, background: team.status === 'locked' ? '#ef4444' : '#22c55e', height: '100%' }} />
                                        </div>
                                    </div>

                                    {/* Inline Add Member Search Panel */}
                                    {addMemberTeamId === team.id && (
                                        <div style={{
                                            background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)',
                                            borderRadius: 10, padding: '10px 12px', marginBottom: 10
                                        }}>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', marginBottom: 6 }}>
                                                <UserPlus size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                                                Add by Roll No or Email
                                            </div>
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                <div style={{ position: 'relative', flex: 1 }}>
                                                    <Search size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#666' }} />
                                                    <input
                                                        ref={addSearchRef}
                                                        autoFocus
                                                        value={addSearch}
                                                        onChange={e => { setAddSearch(e.target.value); setAddSearchError(''); }}
                                                        onKeyDown={e => { if (e.key === 'Enter') handleAdminAddMember(team.id); if (e.key === 'Escape') setAddMemberTeamId(null); }}
                                                        placeholder="e.g. 22BD1A0501 or email"
                                                        style={{
                                                            width: '100%', paddingLeft: 28, padding: '6px 8px 6px 26px',
                                                            borderRadius: 6, border: '1px solid #d1fae5',
                                                            background: '#fff', fontSize: 12, boxSizing: 'border-box'
                                                        }}
                                                    />
                                                </div>
                                                <button
                                                    onClick={() => handleAdminAddMember(team.id)}
                                                    disabled={addSearchLoading || !addSearch.trim()}
                                                    style={{
                                                        background: '#22c55e', color: '#fff', border: 'none',
                                                        borderRadius: 6, padding: '6px 14px', fontSize: 12,
                                                        fontWeight: 700, cursor: 'pointer', flexShrink: 0,
                                                        opacity: addSearchLoading || !addSearch.trim() ? 0.6 : 1
                                                    }}
                                                >
                                                    {addSearchLoading ? '...' : 'Add'}
                                                </button>
                                            </div>
                                            {addSearchError && (
                                                <div style={{ color: '#dc2626', fontSize: 11, marginTop: 5 }}>
                                                    ⚠ {addSearchError}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {/* "Move Here" drop zone shown at top when in selection mode */}
                                        {selectedMember && selectedMember.fromTeamId !== team.id && (
                                            <div
                                                onClick={() => handleMoveMember(team.id)}
                                                style={{
                                                    border: '2px dashed #7c3aed', borderRadius: 8, padding: '10px',
                                                    textAlign: 'center', color: '#7c3aed', fontSize: 12, fontWeight: 700,
                                                    cursor: 'pointer', background: 'rgba(124,58,237,0.06)',
                                                    transition: 'all 0.15s'
                                                }}
                                                onMouseOver={e => e.currentTarget.style.background = 'rgba(124,58,237,0.15)'}
                                                onMouseOut={e => e.currentTarget.style.background = 'rgba(124,58,237,0.06)'}
                                            >
                                                ↓ Drop <strong>{selectedMember.name}</strong> here
                                            </div>
                                        )}

                                        {(team.members || []).map(member => {
                                            const isThisSelected = selectedMember?.email === member.email && selectedMember?.fromTeamId === team.id;
                                            return (
                                                <div
                                                    key={member.email}
                                                    onClick={() => isAuthorized && handleSelectMember(member, team)}
                                                    style={{
                                                        padding: 10, borderRadius: 8, fontSize: 13,
                                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                        cursor: isAuthorized ? 'pointer' : 'default',
                                                        transition: 'all 0.15s',
                                                        background: isThisSelected
                                                            ? 'rgba(99,102,241,0.18)'
                                                            : member.is_dummy ? 'rgba(251,191,36,0.07)' : 'rgba(255,255,255,0.6)',
                                                        border: isThisSelected
                                                            ? '2px solid #6366f1'
                                                            : member.is_dummy ? '1px solid rgba(251,191,36,0.3)' : '1px solid rgba(0,0,0,0.05)',
                                                        boxShadow: isThisSelected ? '0 0 0 3px rgba(99,102,241,0.2)' : 'none'
                                                    }}
                                                    onMouseOver={e => { if (isAuthorized && !isThisSelected) e.currentTarget.style.background = member.is_dummy ? 'rgba(251,191,36,0.13)' : 'rgba(0,0,0,0.04)'; }}
                                                    onMouseOut={e => { if (!isThisSelected) e.currentTarget.style.background = member.is_dummy ? 'rgba(251,191,36,0.07)' : 'rgba(255,255,255,0.6)'; }}
                                                >
                                                    <div style={{ overflow: 'hidden', flex: 1 }}>
                                                        <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            {isThisSelected && (
                                                                <span style={{ width: 16, height: 16, background: '#6366f1', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                    <svg width="9" height="9" viewBox="0 0 9 9" fill="none"><path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                                </span>
                                                            )}
                                                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{member.name}</span>
                                                            {member.is_dummy === 1 && (
                                                                <span style={{ fontSize: 9, background: 'rgba(251,191,36,0.2)', color: '#b45309', padding: '1px 5px', borderRadius: 3, fontWeight: 'bold', flexShrink: 0 }}>NOT REG.</span>
                                                            )}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: '#666' }}>
                                                            {member.rollNo && <span style={{ marginRight: 4 }}>{member.rollNo} •</span>}
                                                            {member.department && <span>{member.department}</span>}
                                                            {!member.department && member.is_dummy ? <span style={{ color: '#b45309' }}>Not yet registered</span> : null}
                                                            {member.year ? ` • Year ${member.year}` : ''}
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 8 }}>
                                                        {member.role === 'lead' ? (
                                                            <span style={{ fontSize: 10, background: '#dbeafe', color: '#1e40af', padding: '2px 6px', borderRadius: 4, fontWeight: 'bold' }}>LEAD</span>
                                                        ) : isAuthorized ? (
                                                            <button
                                                                onClick={e => { e.stopPropagation(); handleChangeLead(team.id, member.email, team.team_name); }}
                                                                style={{ border: 'none', background: 'transparent', color: '#666', cursor: 'pointer', fontSize: 11, textDecoration: 'underline' }}
                                                            >
                                                                Promote
                                                            </button>
                                                        ) : null}
                                                        {isThisSelected && (
                                                            <span style={{ fontSize: 10, color: '#6366f1', fontWeight: 700 }}>Selected</span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <button
                                        onClick={() => navigate(`/admin/teams/${taskId}/team/${team.id}`)}
                                        className="btn-profile btn-profile-secondary"
                                        style={{ width: '100%', marginTop: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '13px' }}
                                    >
                                        <ExternalLink size={14} /> View Team Room
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                {showGenerateModal && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <div style={{ background: 'white', borderRadius: 16, padding: 32, width: '90%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}>
                            <h3 style={{ marginBottom: 20, fontSize: '1.5rem', fontWeight: '700', color: '#333' }}>Auto-Generate Teams</h3>
                            
                            <div style={{ background: '#f8fafc', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '2rem' }}>
                                <p style={{ margin: '0 0 12px 0', color: '#475569', fontSize: '0.95rem' }}>
                                    This will smartly group students into teams based on the constraints defined when creating this room. It prioritizes exact department matches and female limits.
                                </p>
                                <ul style={{ paddingLeft: '20px', margin: 0, color: '#334155', fontSize: '0.9rem', lineHeight: '1.6' }}>
                                    <li><strong>Max Members per Team:</strong> {task?.max_members || 6}</li>
                                    <li><strong>Department Composition:</strong> {
                                        task?.rules && typeof task.rules === 'string' ? 
                                        (() => {
                                            try {
                                                const r = JSON.parse(task.rules);
                                                return r.sectionLimits ? Object.entries(r.sectionLimits).map(([d, c]) => `${d}: ${c}`).join(', ') : 'Any';
                                            } catch (e) { return 'Any'; }
                                        })() : 'Any'
                                    }</li>
                                    <li><strong>Min Females per Team:</strong> {
                                        task?.rules && typeof task.rules === 'string' ? 
                                        (() => {
                                            try {
                                                const r = JSON.parse(task.rules);
                                                return r.genderRequirements?.female || 'None';
                                            } catch (e) { return 'None'; }
                                        })() : 'None'
                                    }</li>
                                </ul>
                            </div>
                            
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                                <button
                                    onClick={() => setShowGenerateModal(false)}
                                    className="btn-profile btn-profile-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleGenerateTeams}
                                    disabled={isGenerating}
                                    className="btn-profile btn-profile-primary"
                                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                                >
                                    {isGenerating ? "Generating..." : "Generate Teams & Lock Room"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {isGenerating && <LoadingScreen message="Generating Teams" />}

                {justGenerated && (
                    <div style={{
                        position: 'fixed',
                        bottom: 20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#fff',
                        padding: '12px 24px',
                        borderRadius: 50,
                        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        zIndex: 1000,
                        border: '1px solid #ddd'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e' }}></div>
                            <strong style={{ color: '#333' }}>Generation Complete!</strong>
                        </div>
                        <div style={{ height: 20, width: 1, background: '#ddd' }}></div>
                        <button
                            onClick={handleRevert}
                            style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: 20,
                                cursor: 'pointer',
                                fontWeight: 600
                            }}
                        >
                            Undo / Revert
                        </button>
                        <button
                            onClick={handleSave}
                            style={{
                                background: '#22c55e',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: 20,
                                cursor: 'pointer',
                                fontWeight: 600
                            }}
                        >
                            Save Changes
                        </button>
                    </div>
                )}
        {/* ZIP Export Progress Modal */}
        {zipProgress && (
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000
            }}>
                <div style={{
                    background: '#1E1E1E', borderRadius: 12, padding: 24, width: '90%', maxWidth: 700,
                    border: '1px solid #333', boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                    display: 'flex', flexDirection: 'column', height: '60vh'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <h3 style={{ color: 'white', margin: 0 }}>📦 Exporting ZIP...</h3>
                        <button 
                            onClick={() => setZipProgress(null)}
                            style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', fontSize: 20 }}
                        >✕</button>
                    </div>
                    
                    <div style={{
                        flex: 1, background: '#0D0D0D', borderRadius: 8, padding: 16, overflowY: 'auto',
                        fontFamily: 'monospace', fontSize: 13, color: '#A3A3A3', border: '1px solid #333'
                    }}>
                        {zipProgress.logs.map((log, i) => (
                            <div key={i} style={{ 
                                marginBottom: 6, 
                                color: log.includes('✅') ? '#4ADE80' : log.includes('⚠️') ? '#FBBF24' : log.includes('❌') ? '#EF4444' : '#A3A3A3' 
                            }}>
                                {log}
                            </div>
                        ))}
                        <div ref={zipLogsEndRef} />
                    </div>
                </div>
            </div>
        )}

            </div>
        </div>
        {showReviewersModal && task && (
            <ReviewersModal
                taskId={taskId}
                taskName={task.name}
                requesterEmail={JSON.parse(localStorage.getItem('vjconnect_user') || '{}').email}
                onClose={() => setShowReviewersModal(false)}
            />
        )}
        </>
    );
}




